import React, { useEffect, useState } from 'react';
import useStyles from '../styles';
import { API } from 'aws-amplify';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Form from 'components/form';
import Input from 'components/form/Input';
import ChipInput from 'components/form/ChipInput';
import CheckboxGroup from 'components/form/CheckboxGroup';
import RadioGroup from 'components/form/Radio';
import Select from 'components/form/Select';
import Divider from '@material-ui/core/Divider';
import Button from 'components/common/Button';
import useSnack from 'hooks/useSnack';
import { useMutation } from '@apollo/react-hooks';
import { UPDATE_STUDENT_PROFILE } from 'graphql/student/Profile';
import extend from 'lodash/extend';
import SPECIAL_CASES from 'constant/special-cases';
import FEELING from 'constant/feelings';

const FormItem = Form.FormItem;

const HighSchoolForm = ({
  college_info,
  user,
  saveText,
  isSkip,
  onSkip,
  disableCard,
  form: { getFieldDecorator, validateFields, getFieldValue },
  step,
  history,
  editable,
}) => {
  const classes = useStyles();
  const snack = useSnack();
  const [update] = useMutation(UPDATE_STUDENT_PROFILE);

  const onSubmit = () => {
    return validateFields().then((values) => {
      values = { ...values, completed: true };
      if (values.colleges_interested) {
        values.colleges_interested = values.colleges_interested.map(
          (i) => i.value,
        );
      }
      return update({
        variables: {
          id: user.id,
          input: {
            college_info: extend(college_info, values),
          },
        },
      }).then(
        () => {
          snack.success('Profile saved');
          step && history.push(`/onboarding/${step + 1}`);
        },
        (e) => {
          snack.error(e.message);
        },
      );
    });
  };

  const fetchColleges = (query) => {
    return API.get('api', '/college/search', {
      queryStringParameters: {
        name: query,
        fields: 'id,school.name',
      },
    }).then((resp) => {
      return resp.results.map((i) => ({
        label: i['school.name'],
        value: i['school.name'],
      }));
    });
  };

  const applicantCase =
    getFieldValue('special_applicant_cases') ||
    college_info?.special_applicant_cases;

  return (
    <Card className={classes.card} elevation={disableCard && 0}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h5">College Questions</Typography>
        <Box marginTop={4} maxWidth={500} width="100%">
          <Form>
            <FormItem>
              {getFieldDecorator('colleges_interested', {
                initialValue: college_info?.colleges_interested?.map((i) => ({
                  label: i,
                  value: i,
                })),
                rules: [{ required: true, message: 'This field is required' }],
              })(
                <Select
                  disabled={!editable}
                  async
                  isMulti
                  cacheOptions
                  placeholder="Search for college..."
                  label="What colleges are you interested in?"
                  loadOptions={fetchColleges}
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('apply_early_action', {
                initialValue: college_info?.apply_early_action,
                rules: [{ required: true, message: 'This field is required' }],
              })(
                  <RadioGroup
                      style={{ flexDirection: 'row' }}
                      disabled={!editable}
                      label="Are you planning to apply early action/early decision?"
                      options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                  />,
              )}
            </FormItem>

            <FormItem>
              {getFieldDecorator('majors_interested', {
                initialValue: college_info?.majors_interested,
                rules: [{ required: true, message: 'This field is required' }],
              })(
                <ChipInput
                  disabled={!editable}
                  fullWidth
                  placeholder="Type major and press Enter"
                  label="College Majors"
                  hint="List any college majors you would like to explore. Don’t worry  if you aren’t sure what you want to major in - we just want to have an idea of the types of fields that interest you"
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('special_applicant_cases', {
                initialValue: college_info?.special_applicant_cases,
              })(
                <CheckboxGroup
                  disabled={!editable}
                  className={classes.checkboxText}
                  style={{ marginBottom: 16 }}
                  options={SPECIAL_CASES}
                  setColor={disableCard && 'primary'}
                  label={
                    <Typography
                      color="textPrimary"
                      style={{ paddingBottom: 4 }}
                      className={classes.formItem}
                    >
                      Special College Applicant Cases
                    </Typography>
                  }
                  size="small"
                  hint="The following cases require special expertise and additional materials when applying for college. If you plan to apply for a BS/MD program, submit an art portfolio with your application, or sign as a recruited college athlete, select the appropriate boxes below."
                />,
              )}
            </FormItem>
            {applicantCase?.includes('other') && (
              <FormItem>
                {getFieldDecorator('special_applicant_cases_details', {
                  initialValue: college_info?.special_applicant_cases_details,
                  rules: [{ required: true, message: 'It is required' }],
                })(
                  <Input
                    disabled={!editable}
                    multiline
                    rowsMin={2}
                    rows={2}
                    label={
                      <Typography
                        color="textPrimary"
                        style={{ paddingBottom: 4 }}
                        className={classes.formItem}
                      >
                        Describe your specific need
                      </Typography>
                    }
                    hint="If you have a special college applicant case, please explain it here in more details."
                  />,
                )}
              </FormItem>
            )}
            <br />
            <FormItem>
              {getFieldDecorator('feeling', {
                initialValue: college_info?.feeling,
                validateFirst: true,
              })(
                <CheckboxGroup
                  disabled={!editable}
                  className={classes.checkboxText}
                  style={{ marginBottom: 16 }}
                  options={FEELING}
                  setColor={disableCard && 'primary'}
                  label={
                    <Typography
                      color="textPrimary"
                      style={{ paddingBottom: 4, fontWeight: 500 }}
                    >
                      How do you feel about applying to college?
                    </Typography>
                  }
                  size="small"
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('feeling_details', {
                initialValue: college_info?.feeling_details,
              })(
                <Input
                  disabled={!editable}
                  multiline
                  rowsMin={1}
                  rows={1}
                  label={
                    <Typography
                      color="textPrimary"
                      style={{ paddingBottom: 4, fontWeight: 500 }}
                    >
                      {' '}
                      Describe more about how you feel
                    </Typography>
                  }
                />,
              )}
            </FormItem>
          </Form>
        </Box>
      </CardContent>
      {!disableCard && <Divider />}
      <CardContent className={classes.footer}>
        <Box display="flex" justifyContent="center">
          {isSkip && (
            <div style={{ marginRight: 24 }}>
              <Button size="large" onClick={onSkip} variant="outlined">
                Skip
              </Button>
            </div>
          )}
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={!editable}
          >
            {saveText || 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Form.create()(HighSchoolForm);
